import React, { useEffect, useState } from 'react';
import styles from './DocumentCard.module.scss';
import { IDocumentCard } from './DocumentCard.interface';
import {
  LoadingSpinner,
  PDFHighlightPreview,
  Select,
  Toggle
} from 'app/components';
import { Button } from '@zurich-primitives/button';
import { Tooltip } from 'react-tooltip';
import { Trash } from '@zurich-data-dsm-icons/action.trash';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/AttachWhite.svg';
import { ReactComponent as CloseIcon } from 'app/assets/Icons/Close.svg';
import FileIcon from 'app/assets/Icons/DocumentBlank.svg';
import classnames from 'classnames';
import { getDocumentViaProxy } from 'integration';

export const DocumentCard: React.FC<IDocumentCard> = ({
  title,
  mode,
  onModeChange,
  selectedFile,
  errorText,
  fileHandlers,
  isDragging,
  documentList,
  duplicateDocument
}) => {
  const MAX_TEXT_DISPLAY = 30;
  const [selectedDocument, setSelectedDocument] = useState('');
  const isLoading = selectedDocument && !selectedFile;

  useEffect(() => {
    if (selectedDocument) {
      const doc = documentList.find((d) => d.documentURL === selectedDocument);

      getDocumentViaProxy(
        'cross-functional',
        'document-compare',
        selectedDocument
      )
        .then((blob) => {
          const file = new File([blob], doc!.documentName, {
            type: 'application/pdf'
          });

          const document = {
            file,
            documentId: doc!.documentID,
            shouldUpload: false
          };

          fileHandlers.onDocumentSelect(document);

          if (duplicateDocument) {
            fileHandlers.clearDuplicate();
          }
        })
        .catch((error) => console.error(error));
    }
  }, [selectedDocument]);

  const renderFileSection = (): JSX.Element | null => {
    if (!selectedFile || mode === 'right') return null;

    return (
      <div className={styles['file-section']}>
        <div className={styles['file-section']}>
          <img src={FileIcon} alt="File Icon" aria-hidden />
          <span
            className={styles['file-name']}
            {...(selectedFile.file.name.length > MAX_TEXT_DISPLAY && {
              'data-tooltip-id': 'file-title-tooltip',
              'data-tooltip-content': selectedFile.file.name
            })}
          >
            {selectedFile.file.name.length > MAX_TEXT_DISPLAY
              ? `${selectedFile.file.name.slice(0, MAX_TEXT_DISPLAY)}...`
              : selectedFile.file.name}
          </span>
          <Button
            onClick={handleDelete}
            className={styles['delete-button']}
            aria-label="Delete file"
            size="small"
            variant="icon"
          >
            <Button.Icon>
              <Trash />
            </Button.Icon>
          </Button>

          <Tooltip
            id="file-title-tooltip"
            className={styles['shortened-title-tooltip']}
            noArrow
            opacity={1}
          />
        </div>
      </div>
    );
  };

  const handleDelete = (): void => {
    setSelectedDocument('');
    fileHandlers.handleDeleteFile();
  };

  const renderUploadSection = (): JSX.Element => {
    let content: JSX.Element;
    if (selectedFile) {
      content = (
        <PDFHighlightPreview
          highlightCoords={undefined}
          selectedHighlight={undefined}
          showHighlights={false}
          displayFile={selectedFile.file}
        />
      );
    } else if (isLoading) {
      content = (
        <div className={styles['selected-file']}>
          <LoadingSpinner
            containerStyle={{
              position: 'relative',
              width: 'auto',
              height: 'auto',
              backdropFilter: 'unset'
            }}
          />
        </div>
      );
    } else {
      content = (
        <section
          className={classnames(styles['upload-area'], {
            [styles.dragging]: isDragging
          })}
          onDrop={fileHandlers.handleFileDrop}
          onDragOver={fileHandlers.handleDragOver}
          onDragEnter={fileHandlers.handleDragEnter}
          onDragLeave={fileHandlers.handleDragLeave}
          aria-label="File upload area"
        >
          <p className={styles['upload-text']}>
            Browse a file or drag it here to upload
          </p>
          <Button
            onClick={fileHandlers.handleSelectionClick}
            className={styles['upload-button']}
          >
            <Button.Icon>
              <AttachIcon />
            </Button.Icon>
            <Button.Label>Upload File</Button.Label>
          </Button>
          <span className={styles.error}>{errorText}</span>
        </section>
      );
    }
    return <div className={styles['upload-section']}>{content}</div>;
  };

  const renderSelectSection = (): JSX.Element => {
    let content: JSX.Element;
    if (selectedFile) {
      content = (
        <PDFHighlightPreview
          highlightCoords={undefined}
          selectedHighlight={undefined}
          showHighlights={false}
          displayFile={selectedFile.file}
        />
      );
    } else if (isLoading) {
      content = (
        <div className={styles['selected-file']}>
          <LoadingSpinner
            containerStyle={{
              position: 'relative',
              width: 'auto',
              height: 'auto',
              backdropFilter: 'unset'
            }}
          />
        </div>
      );
    } else {
      content = (
        <div className={styles['selected-file']}>
          <p>The selected file will be displayed here</p>
        </div>
      );
    }
    return (
      <div className={styles['select-section']}>
        <Select
          label="Document"
          options={documentList.map((doc) => ({
            value: doc.documentURL,
            label: doc.documentName
          }))}
          selectedValue={selectedDocument}
          onSelect={(option) => setSelectedDocument(option.value)}
          searchable={true}
        />
        {content}
      </div>
    );
  };

  const renderDuplicateWarning = (): JSX.Element => {
    return (
      <div className={styles['duplicate-warning']}>
        <p>
          A document with the name <b>{duplicateDocument!.documentName}</b>{' '}
          already exists. Use existing document or rename and try again.
        </p>

        <Button
          onClick={() => setSelectedDocument(duplicateDocument!.documentURL)}
          aria-label="Use existing document"
          size="small"
        >
          <Button.Label>
            Use existing
            <br />
            document
          </Button.Label>
        </Button>

        <Button
          onClick={() => fileHandlers.clearDuplicate()}
          aria-label="Close button"
          size="small"
          variant="icon"
        >
          <Button.Icon>
            <CloseIcon />
          </Button.Icon>
        </Button>
      </div>
    );
  };

  return (
    <div className={styles['document-card']}>
      <div className={styles['card-header']}>
        <h3 className={styles['card-title']}>{title}</h3>

        <Toggle
          leftLabel="Upload new"
          rightLabel="Find document"
          defaultActive={mode}
          onToggle={onModeChange}
        />

        <p className={styles['file-note']}>
          Please note only Digital PDFs are supported.
        </p>

        {duplicateDocument && renderDuplicateWarning()}

        {renderFileSection()}
      </div>

      <div className={styles['card-content']}>
        {mode === 'left' ? renderUploadSection() : renderSelectSection()}
      </div>
    </div>
  );
};
